import { useEffect, useRef, useState, useContext } from 'react';
import { SimulatorContext } from '../contexts/SimulatorContext';

export interface SafeAreaInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface ViewportMetrics {
  width: number;
  height: number;
  layoutWidth: number;
  layoutHeight: number;
  offsetTop: number;
  offsetLeft: number;
  aspectRatio: number;
  orientation: 'portrait' | 'landscape';
  isTouch: boolean;
  hasVisualViewport: boolean;
  safeArea: SafeAreaInsets;
}

export const defaultViewportMetrics: ViewportMetrics = {
  width: 1280,
  height: 720,
  layoutWidth: 1280,
  layoutHeight: 720,
  offsetTop: 0,
  offsetLeft: 0,
  aspectRatio: 1280 / 720,
  orientation: 'landscape',
  isTouch: false,
  hasVisualViewport: false,
  safeArea: { top: 0, right: 0, bottom: 0, left: 0 },
};

function readSafeAreaInsets(): SafeAreaInsets {
  if (typeof document === 'undefined') {
    return defaultViewportMetrics.safeArea;
  }

  const probe = document.createElement('div');
  probe.style.position = 'fixed';
  probe.style.visibility = 'hidden';
  probe.style.pointerEvents = 'none';
  probe.style.inset = '0';
  probe.style.paddingTop = 'env(safe-area-inset-top, 0px)';
  probe.style.paddingRight = 'env(safe-area-inset-right, 0px)';
  probe.style.paddingBottom = 'env(safe-area-inset-bottom, 0px)';
  probe.style.paddingLeft = 'env(safe-area-inset-left, 0px)';

  document.body.appendChild(probe);
  const styles = window.getComputedStyle(probe);
  const safeArea = {
    top: parseFloat(styles.paddingTop) || 0,
    right: parseFloat(styles.paddingRight) || 0,
    bottom: parseFloat(styles.paddingBottom) || 0,
    left: parseFloat(styles.paddingLeft) || 0,
  };
  document.body.removeChild(probe);

  return safeArea;
}

export function getViewportMetrics(): ViewportMetrics {
  if (typeof window === 'undefined') {
    return defaultViewportMetrics;
  }

  const visualViewport = window.visualViewport;
  const width = Math.round(visualViewport?.width ?? window.innerWidth);
  const height = Math.round(visualViewport?.height ?? window.innerHeight);
  const layoutWidth = Math.round(window.innerWidth);
  const layoutHeight = Math.round(window.innerHeight);
  const offsetTop = Math.round(visualViewport?.offsetTop ?? 0);
  const offsetLeft = Math.round(visualViewport?.offsetLeft ?? 0);
  const aspectRatio = height > 0 ? width / height : 1;
  const orientation = width >= height ? 'landscape' : 'portrait';
  const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  return {
    width,
    height,
    layoutWidth,
    layoutHeight,
    offsetTop,
    offsetLeft,
    aspectRatio,
    orientation,
    isTouch,
    hasVisualViewport: Boolean(visualViewport),
    safeArea: typeof document !== 'undefined' ? readSafeAreaInsets() : defaultViewportMetrics.safeArea,
  };
}

function areInsetsEqual(a: SafeAreaInsets, b: SafeAreaInsets): boolean {
  return a.top === b.top && a.right === b.right && a.bottom === b.bottom && a.left === b.left;
}

function areMetricsEqual(a: ViewportMetrics, b: ViewportMetrics): boolean {
  return (
    a.width === b.width &&
    a.height === b.height &&
    a.layoutWidth === b.layoutWidth &&
    a.layoutHeight === b.layoutHeight &&
    a.offsetTop === b.offsetTop &&
    a.offsetLeft === b.offsetLeft &&
    a.aspectRatio === b.aspectRatio &&
    a.orientation === b.orientation &&
    a.isTouch === b.isTouch &&
    a.hasVisualViewport === b.hasVisualViewport &&
    areInsetsEqual(a.safeArea, b.safeArea)
  );
}

export function useViewportMetrics(): ViewportMetrics {
  const { mockViewport } = useContext(SimulatorContext);
  const [metrics, setMetrics] = useState<ViewportMetrics>(() => mockViewport || getViewportMetrics());
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    if (mockViewport) {
      setMetrics(mockViewport);
      return;
    }

    const commitMetrics = () => {
      const nextMetrics = getViewportMetrics();
      setMetrics((currentMetrics) => (areMetricsEqual(currentMetrics, nextMetrics) ? currentMetrics : nextMetrics));
    };

    const scheduleMetricsUpdate = () => {
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
      }
      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = null;
        commitMetrics();
      });
    };

    commitMetrics();

    const visualViewport = window.visualViewport;
    window.addEventListener('resize', scheduleMetricsUpdate);
    window.addEventListener('orientationchange', scheduleMetricsUpdate);
    visualViewport?.addEventListener('resize', scheduleMetricsUpdate);
    visualViewport?.addEventListener('scroll', scheduleMetricsUpdate);

    return () => {
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
      }
      window.removeEventListener('resize', scheduleMetricsUpdate);
      window.removeEventListener('orientationchange', scheduleMetricsUpdate);
      visualViewport?.removeEventListener('resize', scheduleMetricsUpdate);
      visualViewport?.removeEventListener('scroll', scheduleMetricsUpdate);
    };
  }, [mockViewport]);

  return metrics;
}
